/* assets/littleg-kid-mapbox-runtime.js — LITTLEG-KID-MAPBOX-RUNTIME-01
 * Mapbox owns:
 * - basemap
 * - geographic projection
 * - camera
 * - map rendering engine
 *
 * LittleG owns:
 * - friend identity
 * - family/place semantics
 * - selected MTR anchors
 * - pins and clusters
 * - relationship lines
 * - constellation visuals
 * - search markers
 * - station-picking states
 * - curated nearby POI
 * - missions and rewards
 * - selection/focus states
 * - all child-facing interaction states
 *
 * Mapbox POI styling must never become
 * the primary LittleG product interface.
 */

(function (window, document) {
  'use strict';

  var VERSION = '1.2.3';
  var CONFIG = '[data-littleg-kid-map-config]';
  var CANVAS = '[data-gproperty-map-canvas]';
  var RELATIONSHIP_SOURCE = 'littleg-kid-relationship-source';
  var RELATIONSHIP_LINE_LAYER = 'littleg-kid-relationship-line';
  var SPOTLIGHT_SOURCE = 'littleg-kid-selected-area-source';
  var SPOTLIGHT_LAYER = 'littleg-kid-selected-area-spotlight';
  var LOCATION_SOURCE = 'littleg-kid-location-source';
  var LOCATION_ACCURACY_LAYER = 'littleg-kid-location-accuracy';
  var LOCATION_POINT_LAYER = 'littleg-kid-location-point';
  var BASEMAP_IMPORT_ID = 'basemap';
  var BASEMAP_CONTRACT = 'littleg-city-v1';
  var OVERLAY_CONTRACT = 'littleg-overlay-v1';
  var OVERLAY_FAMILIES = Object.freeze({
    context: 'context',
    social: 'social',
    active: 'active',
    story: 'story',
  });
  var OVERLAY_STATES = Object.freeze({
    default: 'default',
    hover: 'hover',
    focused: 'focused',
    selected: 'selected',
    temporary: 'temporary',
    new: 'new',
    completed: 'completed',
    disabled: 'disabled',
  });
  var BASEMAP_CONFIG = Object.freeze({
    showPointOfInterestLabels: false,
    showLandmarkIcons: false,
    showLandmarkIconLabels: false,
    showIndoorLabels: false,
    showTransitLabels: false,
    showHdRoads: false,
    showPedestrianRoads: false,
    show3dObjects: false,
    colorBuildings: '#d9e2de',
    colorLand: '#f7f2e8',
    colorWater: '#cfe8f3',
    showPlaceLabels: true,
    colorPlaceLabels: '#52616f',
  });
  var NIGHT_BASEMAP_CONFIG = Object.freeze({
    colorBuildings: '#26363b',
    colorLand: '#182126',
    colorWater: '#102f3a',
    colorPlaceLabels: '#d8e2e7',
  });

  var LITTLE_G_LAYER_ORDER = Object.freeze([
    LOCATION_ACCURACY_LAYER,
    LOCATION_POINT_LAYER,
    SPOTLIGHT_LAYER,
    RELATIONSHIP_LINE_LAYER,
  ]);

  function basemapConfigFor(root) {
    var next = Object.assign({}, BASEMAP_CONFIG);

    if (root && root.getAttribute('data-kid-night-mode') === 'on') {
      Object.keys(NIGHT_BASEMAP_CONFIG).forEach(function (key) {
        next[key] = NIGHT_BASEMAP_CONFIG[key];
      });
    }

    return next;
  }

  function projectDomOverlayFamily(element, family, role) {
    if (!element) return element;

    element.setAttribute(
      'data-kid-overlay-family',
      OVERLAY_FAMILIES[family] || OVERLAY_FAMILIES.context
    );

    if (role) {
      element.setAttribute('data-kid-overlay-role', String(role));
    }

    return element;
  }

  function projectDomOverlayState(element, state) {
    if (!element) return element;

    var next = OVERLAY_STATES[state] || OVERLAY_STATES.default;

    element.setAttribute('data-kid-overlay-state', next);

    element.classList.toggle('is-selected', next === OVERLAY_STATES.selected);

    return element;
  }

  function setMarkerOverlayState(marker, state) {
    if (!marker || typeof marker.getElement !== 'function') {
      return false;
    }

    var element = marker.getElement();

    if (!element) return false;

    projectDomOverlayState(element, state);

    return true;
  }

  function config() {
    return document.querySelector(CONFIG);
  }

  function applyBasemapContract(root, map) {
    if (!root) return false;
    var values = basemapConfigFor(root);
    var keys = Object.keys(values);
    root.setAttribute('data-kid-basemap-contract', BASEMAP_CONTRACT);
    if (
      !map ||
      typeof map.setConfigProperty !== 'function' ||
      typeof map.getConfigProperty !== 'function'
    ) {
      root.setAttribute('data-kid-basemap-contract-state', 'unsupported');
      root.setAttribute('data-kid-basemap-config-count', '0');
      root.setAttribute('data-kid-basemap-mismatch-keys', keys.join(','));
      return false;
    }

    var verified = 0;
    var mismatch = [];
    keys.forEach(function (key) {
      try {
        map.setConfigProperty(BASEMAP_IMPORT_ID, key, values[key]);
        if (map.getConfigProperty(BASEMAP_IMPORT_ID, key) === values[key]) {
          verified += 1;
        } else {
          mismatch.push(key);
        }
      } catch (_) {
        mismatch.push(key);
      }
    });

    root.setAttribute(
      'data-kid-basemap-contract-state',
      verified === keys.length ? 'applied' : 'partial'
    );
    root.setAttribute('data-kid-basemap-config-count', String(verified));
    root.setAttribute('data-kid-basemap-mismatch-keys', mismatch.join(','));
    return verified === keys.length;
  }

  function setNightMode(root, enabled) {
    var map = mapFor(root);

    if (!root || !map || typeof map.setConfigProperty !== 'function') {
      return false;
    }

    try {
      root.setAttribute('data-kid-night-mode', enabled ? 'on' : 'off');

      map.setConfigProperty(BASEMAP_IMPORT_ID, 'lightPreset', enabled ? 'night' : 'day');

      return applyBasemapContract(root, map);
    } catch (_) {
      return false;
    }
  }

  function destroy(root) {
    var workspace = workspaceFor(root);
    if (!root || !workspace) return false;

    clearFriendMarkers(root);
    clearFriendClusters(root);
    clearSearchMarkers(root);
    clearNearbyPoints(root);
    clearRelationshipLine(root);
    clearSelectedAreaSpotlight(root);
    clearUserLocation(root);

    var map = mapFor(root);

    if (map && typeof map.remove === 'function') {
      map.remove();
    }

    workspace.__map = null;
    workspace.__mapRenderer = '';

    root.removeAttribute('data-kid-map-renderer');
    root.removeAttribute('data-kid-canvas-state');
    root.setAttribute('data-kid-map-ready', 'false');

    return true;
  }

  function resize(root) {
    var map = mapFor(root);

    if (!map) {
      return false;
    }

    map.resize();

    return true;
  }

  // 1. 獲取地圖實例
  function mapFor(root) {
    var workspace = workspaceFor(root);

    if (!workspace || workspace.__mapRenderer !== 'mapbox-gl') {
      return null;
    }

    return workspace.__map || null;
  }

  function enforceLayerOrder(root) {
    var map = mapFor(root);

    if (!map || typeof map.getLayer !== 'function' || typeof map.moveLayer !== 'function') {
      return false;
    }

    LITTLE_G_LAYER_ORDER.forEach(function (layerId) {
      try {
        if (map.getLayer(layerId)) {
          map.moveLayer(layerId);
        }
      } catch (_) {}
    });

    return true;
  }

  function workspaceFor(root) {
    if (!root) return null;

    return root.matches && root.matches('[data-gproperty-map-workspace]')
      ? root
      : root.querySelector('[data-gproperty-map-workspace]');
  }

  function setCanvasState(root, state) {
    if (!(root instanceof HTMLElement)) return;
    var canvas = root.querySelector(CANVAS);
    var status = root.querySelector('[data-gproperty-map-map-status]');
    root.setAttribute('data-kid-canvas-state', state);
    root.setAttribute('data-kid-map-ready', state === 'ready' ? 'true' : 'false');
    if (canvas) canvas.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (!status) return;
    if (state === 'ready') {
      status.textContent = '';
      status.hidden = true;
      return;
    }
    var language = document.documentElement.lang || '';
    status.textContent =
      state === 'error'
        ? /^zh-CN/i.test(language)
          ? '地图暂时无法载入，仍可使用搜索。'
          : /^zh/i.test(language)
            ? '地圖暫時未能載入，仍可使用搜尋。'
            : 'Map unavailable. You can still search.'
        : /^zh-CN/i.test(language)
          ? '地图载入中…'
          : /^zh/i.test(language)
            ? '地圖載入中…'
            : 'Loading map…';
    status.hidden = false;
  }

  // Initial readiness belongs to Mapbox, not to the product controller.
  // Mapbox Standard exposes its basemap through an import, not always layers.
  function mapReadyConditions(root, map) {
    var workspace = workspaceFor(root);
    var container = workspace && workspace.querySelector(CANVAS);
    if (
      !root ||
      !map ||
      !container ||
      typeof map.loaded !== 'function' ||
      !map.loaded() ||
      typeof map.isStyleLoaded !== 'function' ||
      !map.isStyleLoaded() ||
      root.getAttribute('data-kid-basemap-contract-state') !== 'applied'
    )
      return false;
    var style = typeof map.getStyle === 'function' ? map.getStyle() : null;
    var hasLayers = !!style && Array.isArray(style.layers) && style.layers.length > 0;
    var hasBasemap =
      !!style &&
      Array.isArray(style.imports) &&
      style.imports.some(function (entry) {
        return entry && entry.id === BASEMAP_IMPORT_ID;
      });
    if (!hasLayers && !hasBasemap) return false;
    var canvas = typeof map.getCanvas === 'function' ? map.getCanvas() : null;
    var bounds =
      typeof container.getBoundingClientRect === 'function'
        ? container.getBoundingClientRect()
        : null;
    return (
      !!canvas &&
      canvas.width > 0 &&
      canvas.height > 0 &&
      !!bounds &&
      bounds.width > 0 &&
      bounds.height > 0
    );
  }

  function settleMapReadiness(root, map) {
    if (!root || mapFor(root) !== map) return false;
    if (mapReadyConditions(root, map)) {
      setCanvasState(root, 'ready');
      return true;
    }
    if (root.getAttribute('data-kid-canvas-state') !== 'error') {
      setCanvasState(root, 'loading');
    }
    return false;
  }

  // 2. 掛載地圖
  function mount(root) {
    var workspace = workspaceFor(root);

    if (!workspace) return null;

    if (workspace.__map) {
      return workspace.__mapRenderer === 'mapbox-gl' ? workspace.__map : null;
    }

    var node = config();

    var canvas = workspace.querySelector(CANVAS);

    if (!node || !canvas || !window.mapboxgl) {
      return null;
    }

    var token = node.getAttribute('data-mapbox-public-token') || '';

    var styleUrl = node.getAttribute('data-mapbox-style-url') || '';

    if (
      !token ||
      !styleUrl ||
      token.indexOf('pk.') !== 0 ||
      styleUrl.indexOf('mapbox://styles/') !== 0
    ) {
      setCanvasState(root, 'error');
      return null;
    }

    setCanvasState(root, 'loading');
    var map;
    try {
      map = new window.mapboxgl.Map({
        container: canvas,

        accessToken: token,

        style: styleUrl,

        center: [114.1694, 22.3193],

        zoom: 12,

        attributionControl: true,

        dragPan: true,
        keyboard: true,
        scrollZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        dragRotate: false,
        touchZoomRotate: true,
        touchPitch: false,
      });
    } catch (_) {
      setCanvasState(root, 'error');
      return null;
    }

    if (map.touchZoomRotate && typeof map.touchZoomRotate.disableRotation === 'function') {
      map.touchZoomRotate.disableRotation();
    }

    if (map.keyboard && typeof map.keyboard.disableRotation === 'function') {
      map.keyboard.disableRotation();
    }

    map.on('style.load', function () {
      if (mapFor(root) !== map) return;
      setCanvasState(root, 'loading');
      applyBasemapContract(root, map);
      settleMapReadiness(root, map);
    });

    map.on('load', function () {
      settleMapReadiness(root, map);
    });
    map.on('idle', function () {
      if (root.getAttribute('data-kid-map-ready') !== 'true') {
        settleMapReadiness(root, map);
      }
    });
    map.on('error', function () {
      if (root.getAttribute('data-kid-canvas-state') !== 'ready') {
        setCanvasState(root, 'error');
      }
    });

    workspace.__map = map;
    workspace.__mapRenderer = 'mapbox-gl';

    root.setAttribute('data-kid-map-renderer', 'mapbox-gl');

    root.setAttribute('data-kid-overlay-contract', OVERLAY_CONTRACT);

    root.__kidSocialMarkersById = root.__kidSocialMarkersById || Object.create(null);

    root.__kidFriendClustersById = root.__kidFriendClustersById || Object.create(null);

    return map;
  }

  // 4. 縮放控制
  function getZoom(root) {
    var map = mapFor(root);

    return map && typeof map.getZoom === 'function' ? Number(map.getZoom()) || 0 : 0;
  }
  function setView(root, options) {
    var map = mapFor(root);

    if (!map || !options) {
      return false;
    }

    var lat = Number(options.lat);

    var lng = Number(options.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return false;
    }

    map.easeTo({
      center: [lng, lat],

      zoom: Number.isFinite(Number(options.zoom)) ? Number(options.zoom) : map.getZoom(),

      duration: 350,
    });

    return true;
  }

  // 3. 統一的視角飛越 (FlyTo)
  function flyTo(root, options) {
    var map = mapFor(root);

    if (!map || !options) {
      return false;
    }

    var lat = Number(options.lat);

    var lng = Number(options.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return false;
    }

    map.flyTo({
      center: [lng, lat],

      zoom: Number.isFinite(Number(options.zoom))
        ? Number(options.zoom)
        : Math.max(Number(map.getZoom()) || 0, 15),

      duration: 550,

      essential: false,
    });

    return true;
  }

  function fitBounds(root, options) {
    var map = mapFor(root);

    var points = options && Array.isArray(options.points) ? options.points : [];

    if (!map || !points.length) {
      return false;
    }

    var coordinates = points
      .map(function (point) {
        var lat = Number(point && point.lat);

        var lng = Number(point && point.lng);

        return Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : null;
      })
      .filter(Boolean);

    if (!coordinates.length) {
      return false;
    }

    var maxZoom = Number.isFinite(Number(options && options.maxZoom))
      ? Number(options.maxZoom)
      : 16;

    if (coordinates.length === 1) {
      return setView(root, {
        lat: coordinates[0][1],
        lng: coordinates[0][0],
        zoom: Math.min(Math.max(Number(map.getZoom()) || 0, 15), maxZoom),
      });
    }

    var minLng = coordinates[0][0];

    var maxLng = coordinates[0][0];

    var minLat = coordinates[0][1];

    var maxLat = coordinates[0][1];

    coordinates.forEach(function (coordinate) {
      minLng = Math.min(minLng, coordinate[0]);

      maxLng = Math.max(maxLng, coordinate[0]);

      minLat = Math.min(minLat, coordinate[1]);

      maxLat = Math.max(maxLat, coordinate[1]);
    });

    if (minLng === maxLng && minLat === maxLat) {
      return setView(root, {
        lat: minLat,
        lng: minLng,
        zoom: Math.min(Math.max(Number(map.getZoom()) || 0, 15), maxZoom),
      });
    }

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: Number.isFinite(Number(options && options.padding)) ? Number(options.padding) : 48,

        maxZoom: maxZoom,

        duration: 450,
      }
    );

    return true;
  }

  function ensureMarkerHorizontalSafe(root, marker, options) {
    var map = mapFor(root);

    if (
      !map ||
      !marker ||
      typeof marker.getLngLat !== 'function' ||
      typeof map.project !== 'function' ||
      typeof map.unproject !== 'function' ||
      typeof map.getCenter !== 'function' ||
      typeof map.getCanvasContainer !== 'function'
    ) {
      return false;
    }

    var container = map.getCanvasContainer();

    if (!container) return false;

    var width = Number(container.clientWidth) || 0;

    if (width <= 0) return false;

    var padding = Number.isFinite(Number(options && options.padding))
      ? Math.max(48, Number(options.padding))
      : 72;

    var point = map.project(marker.getLngLat());

    if (!point) return false;

    var shiftX = 0;

    if (point.x < padding) {
      shiftX = padding - point.x;
    } else if (point.x > width - padding) {
      shiftX = width - padding - point.x;
    }

    if (Math.abs(shiftX) < 1) {
      return true;
    }

    var centerPoint = map.project(map.getCenter());

    var nextCenter = map.unproject([centerPoint.x - shiftX, centerPoint.y]);

    map.easeTo({
      center: nextCenter,
      duration: Number.isFinite(Number(options && options.duration))
        ? Number(options.duration)
        : 220,
      essential: false,
    });

    return true;
  }

  function panBy(root, options) {
    var map = mapFor(root);

    if (!map || !options) {
      return false;
    }

    var x = Number(options.x);

    var y = Number(options.y);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return false;
    }

    map.panBy([x, y], {
      duration: Number.isFinite(Number(options.duration)) ? Number(options.duration) : 250,
    });

    return true;
  }

  function zoomMotionDuration() {
    try {
      return typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : 250;
    } catch (_) {
      return 250;
    }
  }

  function zoomIn(root) {
    var map = mapFor(root);

    if (!map) return false;

    map.zoomIn({
      duration: zoomMotionDuration(),
    });

    return true;
  }

  function zoomOut(root) {
    var map = mapFor(root);

    if (!map) return false;

    map.zoomOut({
      duration: zoomMotionDuration(),
    });

    return true;
  }

  function clearSelectedAreaSpotlight(root) {
    if (!root) return false;

    root.__kidSpotlightGeneration = (Number(root.__kidSpotlightGeneration) || 0) + 1;

    var map = mapFor(root);
    if (!map) {
      root.removeAttribute('data-kid-selected-area-spotlight');
      return true;
    }

    try {
      if (typeof map.getLayer === 'function' && map.getLayer(SPOTLIGHT_LAYER)) {
        map.removeLayer(SPOTLIGHT_LAYER);
      }
    } catch (_) {}

    try {
      if (typeof map.getSource === 'function' && map.getSource(SPOTLIGHT_SOURCE)) {
        map.removeSource(SPOTLIGHT_SOURCE);
      }
    } catch (_) {}

    root.removeAttribute('data-kid-selected-area-spotlight');

    return true;
  }

  function setSelectedAreaSpotlight(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options) {
      return false;
    }

    var lat = Number(options.lat);
    var lng = Number(options.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return false;
    }

    clearSelectedAreaSpotlight(root);

    var generation = (Number(root.__kidSpotlightGeneration) || 0) + 1;

    root.__kidSpotlightGeneration = generation;

    var geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            overlay_family: 'active',
            overlay_role: 'selected-area-spotlight',
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat],
          },
        },
      ],
    };

    function draw() {
      if (root.__kidSpotlightGeneration !== generation || mapFor(root) !== map) {
        return false;
      }

      try {
        var source = typeof map.getSource === 'function' ? map.getSource(SPOTLIGHT_SOURCE) : null;

        if (source && typeof source.setData === 'function') {
          source.setData(geojson);
        } else if (!source) {
          map.addSource(SPOTLIGHT_SOURCE, {
            type: 'geojson',
            data: geojson,
          });
        }

        if (typeof map.getLayer !== 'function' || !map.getLayer(SPOTLIGHT_LAYER)) {
          map.addLayer({
            id: SPOTLIGHT_LAYER,
            type: 'circle',
            source: SPOTLIGHT_SOURCE,
            metadata: {
              'littleg:overlay-family': 'active',
              'littleg:overlay-role': 'selected-area-spotlight',
              'littleg:overlay-order': 310,
            },
            paint: {
              'circle-radius': 72,
              'circle-color': '#79B86A',
              'circle-opacity': 0.12,
              'circle-blur': 0.72,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#79B86A',
              'circle-stroke-opacity': 0.28,
            },
          });
        }

        enforceLayerOrder(root);

        root.setAttribute('data-kid-selected-area-spotlight', 'true');

        return true;
      } catch (_) {
        return false;
      }
    }

    function retry(attempt) {
      if (root.__kidSpotlightGeneration !== generation || mapFor(root) !== map) {
        return;
      }

      if (draw()) return;
      if (attempt >= 40) return;

      window.setTimeout(function () {
        retry(attempt + 1);
      }, 50);
    }

    retry(0);
    return true;
  }

  function clearRelationshipLine(root) {
    if (!root) {
      return false;
    }

    root.__kidRelationshipGeneration = (Number(root.__kidRelationshipGeneration) || 0) + 1;

    var labelMarker = root.__kidRelationshipLabelMarker;

    if (labelMarker && typeof labelMarker.remove === 'function') {
      labelMarker.remove();
    }

    root.__kidRelationshipLabelMarker = null;

    var map = mapFor(root);

    if (!map) {
      return true;
    }

    try {
      if (typeof map.getLayer === 'function' && map.getLayer(RELATIONSHIP_LINE_LAYER)) {
        map.removeLayer(RELATIONSHIP_LINE_LAYER);
      }
    } catch (_) {}

    try {
      if (typeof map.getSource === 'function' && map.getSource(RELATIONSHIP_SOURCE)) {
        map.removeSource(RELATIONSHIP_SOURCE);
      }
    } catch (_) {}

    return true;
  }

  function setRelationshipLine(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options || !window.mapboxgl) {
      return false;
    }

    var fromLat = Number(options.from && options.from.lat);

    var fromLng = Number(options.from && options.from.lng);

    var toLat = Number(options.to && options.to.lat);

    var toLng = Number(options.to && options.to.lng);

    if (
      !Number.isFinite(fromLat) ||
      !Number.isFinite(fromLng) ||
      !Number.isFinite(toLat) ||
      !Number.isFinite(toLng)
    ) {
      return false;
    }

    clearRelationshipLine(root);

    var generation = (Number(root.__kidRelationshipGeneration) || 0) + 1;

    root.__kidRelationshipGeneration = generation;

    var label = String(options.label || '').trim();

    var geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            overlay_family: 'active',
            overlay_role: 'relationship-line',
          },
          geometry: {
            type: 'LineString',
            coordinates: [
              [fromLng, fromLat],
              [toLng, toLat],
            ],
          },
        },
      ],
    };

    function draw() {
      if (root.__kidRelationshipGeneration !== generation || mapFor(root) !== map) {
        return;
      }

      try {
        map.addSource(RELATIONSHIP_SOURCE, {
          type: 'geojson',
          data: geojson,
        });

        map.addLayer({
          id: RELATIONSHIP_LINE_LAYER,
          type: 'line',
          source: RELATIONSHIP_SOURCE,
          metadata: {
            'littleg:overlay-family': 'active',
            'littleg:overlay-role': 'relationship-line',
            'littleg:overlay-order': 320,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#3478F6',
            'line-width': 3,
            'line-opacity': 0.8,
            'line-dasharray': [2, 2],
          },
        });

        enforceLayerOrder(root);
      } catch (_) {
        return;
      }

      if (!label) {
        return;
      }

      var element = document.createElement('div');

      element.className = 'kid-distance-tooltip';

      projectDomOverlayFamily(element, 'active', 'relationship-label');
      projectDomOverlayState(element, 'default');

      element.textContent = label;

      element.setAttribute('role', 'status');

      var marker = new window.mapboxgl.Marker({
        element: element,
        anchor: 'center',
      })
        .setLngLat([(fromLng + toLng) / 2, (fromLat + toLat) / 2])
        .addTo(map);

      if (root.__kidRelationshipGeneration !== generation) {
        marker.remove();
        return;
      }

      root.__kidRelationshipLabelMarker = marker;
    }

    if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
      if (typeof map.once === 'function') {
        map.once('style.load', draw);
        return true;
      }

      return false;
    }

    draw();

    return true;
  }

  function clearUserLocation(root) {
    if (!root) {
      return false;
    }

    root.__kidLocationGeneration = (Number(root.__kidLocationGeneration) || 0) + 1;

    var map = mapFor(root);

    if (!map) {
      return true;
    }

    [LOCATION_POINT_LAYER, LOCATION_ACCURACY_LAYER].forEach(function (layerId) {
      try {
        if (typeof map.getLayer === 'function' && map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      } catch (_) {}
    });

    try {
      if (typeof map.getSource === 'function' && map.getSource(LOCATION_SOURCE)) {
        map.removeSource(LOCATION_SOURCE);
      }
    } catch (_) {}

    return true;
  }

  function locationAccuracyPolygon(lat, lng, radius) {
    if (!Number.isFinite(radius) || radius <= 0) {
      return null;
    }

    var earthRadius = 6378137;

    var angularDistance = radius / earthRadius;

    var latitude = (lat * Math.PI) / 180;

    var longitude = (lng * Math.PI) / 180;

    var coordinates = [];

    for (var index = 0; index <= 48; index += 1) {
      var bearing = (index / 48) * Math.PI * 2;

      var pointLatitude = Math.asin(
        Math.sin(latitude) * Math.cos(angularDistance) +
          Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
      );

      var pointLongitude =
        longitude +
        Math.atan2(
          Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
          Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(pointLatitude)
        );

      coordinates.push([(pointLongitude * 180) / Math.PI, (pointLatitude * 180) / Math.PI]);
    }

    return {
      type: 'Feature',
      properties: {
        kind: 'accuracy',
        overlay_family: 'context',
        overlay_role: 'user-location-accuracy',
      },
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    };
  }

  function setUserLocation(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options) {
      return false;
    }

    var lat = Number(options.lat);

    var lng = Number(options.lng);

    var accuracy = Number(options.accuracy);

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return false;
    }

    accuracy =
      Number.isFinite(accuracy) && accuracy > 0 ? Math.min(Math.max(accuracy, 5), 5000) : 0;

    clearUserLocation(root);

    var generation = (Number(root.__kidLocationGeneration) || 0) + 1;

    root.__kidLocationGeneration = generation;

    var features = [];

    var accuracyFeature = locationAccuracyPolygon(lat, lng, accuracy);

    if (accuracyFeature) {
      features.push(accuracyFeature);
    }

    features.push({
      type: 'Feature',
      properties: {
        kind: 'point',
        overlay_family: 'context',
        overlay_role: 'user-location-point',
      },
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
    });

    var geojson = {
      type: 'FeatureCollection',
      features: features,
    };

    function draw() {
      if (root.__kidLocationGeneration !== generation || mapFor(root) !== map) {
        return;
      }

      try {
        map.addSource(LOCATION_SOURCE, {
          type: 'geojson',
          data: geojson,
        });

        if (accuracyFeature) {
          map.addLayer({
            id: LOCATION_ACCURACY_LAYER,
            type: 'fill',
            source: LOCATION_SOURCE,
            metadata: {
              'littleg:overlay-family': 'context',
              'littleg:overlay-role': 'user-location-accuracy',
              'littleg:overlay-order': 120,
            },
            filter: ['==', ['get', 'kind'], 'accuracy'],
            paint: {
              'fill-color': '#3478F6',
              'fill-opacity': 0.12,
            },
          });
        }

        map.addLayer({
          id: LOCATION_POINT_LAYER,
          type: 'circle',
          source: LOCATION_SOURCE,
          metadata: {
            'littleg:overlay-family': 'context',
            'littleg:overlay-role': 'user-location-point',
            'littleg:overlay-order': 121,
          },
          filter: ['==', ['get', 'kind'], 'point'],
          paint: {
            'circle-radius': 8,
            'circle-color': '#3478F6',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 4,
          },
        });

        enforceLayerOrder(root);
      } catch (_) {
        return;
      }
    }

    if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
      if (typeof map.once === 'function') {
        map.once('style.load', draw);

        return true;
      }

      return false;
    }

    draw();

    return true;
  }

  function clearSearchMarkers(root) {
    if (!root || !root.__kidSearchMarkersById) {
      return true;
    }

    Object.keys(root.__kidSearchMarkersById).forEach(function (id) {
      var marker = root.__kidSearchMarkersById[id];

      if (marker && typeof marker.remove === 'function') {
        marker.remove();
      }
    });

    root.__kidSearchMarkersById = Object.create(null);

    return true;
  }

  function addSearchMarker(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options || !window.mapboxgl) {
      return null;
    }

    var lat = Number(options.lat);

    var lng = Number(options.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    var id = String(options.id || '').trim();

    root.__kidSearchMarkersById = root.__kidSearchMarkersById || Object.create(null);

    if (
      id &&
      root.__kidSearchMarkersById[id] &&
      typeof root.__kidSearchMarkersById[id].remove === 'function'
    ) {
      root.__kidSearchMarkersById[id].remove();
    }

    var element = document.createElement('div');

    element.className = 'gproperty-map-marker-shell';

    projectDomOverlayFamily(element, 'context', 'search-marker');
    projectDomOverlayState(element, options.selected ? 'selected' : 'temporary');

    var label = document.createElement('span');

    label.className = 'gproperty-map-marker' + (options.selected ? ' is-active' : '');

    label.textContent = String(options.label || '');

    element.appendChild(label);

    if (options.ariaLabel) {
      element.setAttribute('aria-label', String(options.ariaLabel));

      element.setAttribute('title', String(options.ariaLabel));
    }

    if (typeof options.onClick === 'function') {
      element.setAttribute('role', 'button');

      element.tabIndex = 0;

      function activate(event) {
        event.preventDefault();
        event.stopPropagation();
        options.onClick();
      }

      element.addEventListener('click', activate);

      element.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        activate(event);
      });
    }

    var marker = new window.mapboxgl.Marker({
      element: element,
      anchor: 'center',
    })
      .setLngLat([lng, lat])
      .addTo(map);

    if (id) {
      root.__kidSearchMarkersById[id] = marker;
    }

    return marker;
  }

  function clearNearbyPoints(root, type) {
    if (!root || !root.__kidNearbyMarkersByType) {
      return true;
    }

    var key = String(type || '').trim();

    var types = key ? [key] : Object.keys(root.__kidNearbyMarkersByType);

    types.forEach(function (itemType) {
      var markers = root.__kidNearbyMarkersByType[itemType];

      if (Array.isArray(markers)) {
        markers.forEach(function (marker) {
          if (marker && typeof marker.remove === 'function') {
            marker.remove();
          }
        });
      }

      delete root.__kidNearbyMarkersByType[itemType];
    });

    return true;
  }

  function setNearbyPoints(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options || !window.mapboxgl) {
      return false;
    }

    var type = String(options.type || '').trim();

    var points = Array.isArray(options.points) ? options.points : [];

    if (!type) {
      return false;
    }

    clearNearbyPoints(root, type);

    root.__kidNearbyMarkersByType = root.__kidNearbyMarkersByType || Object.create(null);

    var markers = [];

    points.forEach(function (point) {
      var lat = Number(point && point.lat);

      var lng = Number(point && point.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      var isPublicExplore = type === 'littleg-public-explore';

      var element = document.createElement(isPublicExplore ? 'button' : 'div');

      element.className = 'gproperty-map-nearby-marker-shell';

      if (isPublicExplore) {
        element.type = 'button';
        element.classList.add('kid-map-public-pin');
        element.setAttribute('data-kid-public-pin-id', String(point.id || ''));
      }

      projectDomOverlayFamily(element, 'context', 'nearby-point');
      projectDomOverlayState(element, 'default');

      element.setAttribute('data-nearby-type', type);

      var dot = document.createElement('span');

      dot.className = 'gproperty-map-nearby-marker';

      element.appendChild(dot);

      var label = String(point.label || '').trim();

      if (isPublicExplore) {
        element.setAttribute('aria-label', label || 'Public place');

        element.setAttribute('title', label || 'Public place');

        element.setAttribute('aria-pressed', 'false');

        element.addEventListener('click', function (event) {
          event.stopPropagation();

          if (typeof options.onSelect === 'function') {
            options.onSelect(String(point.id || ''));
          }
        });
      } else if (label) {
        element.setAttribute('aria-label', label);

        element.setAttribute('title', label);

        element.setAttribute('role', 'img');

        element.tabIndex = 0;
      }

      var marker = new window.mapboxgl.Marker({
        element: element,
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(map);

      markers.push(marker);
    });

    root.__kidNearbyMarkersByType[type] = markers;

    return true;
  }

  function setPublicExploreSelection(root, id) {
    var selectedId = String(id || '');

    var markers =
      root &&
      root.__kidNearbyMarkersByType &&
      root.__kidNearbyMarkersByType['littleg-public-explore'];

    if (!Array.isArray(markers)) return false;

    markers.forEach(function (marker) {
      var element = marker && typeof marker.getElement === 'function' ? marker.getElement() : null;

      if (!element) return;

      var selected = !!selectedId && element.getAttribute('data-kid-public-pin-id') === selectedId;

      setMarkerOverlayState(marker, selected ? 'selected' : 'default');

      element.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    return true;
  }

  function clearFriendClusters(root) {
    if (!root || !root.__kidFriendClustersById) {
      return true;
    }

    Object.keys(root.__kidFriendClustersById).forEach(function (id) {
      var marker = root.__kidFriendClustersById[id];

      if (marker && typeof marker.remove === 'function') {
        marker.remove();
      }
    });

    root.__kidFriendClustersById = Object.create(null);

    return true;
  }

  function addFriendCluster(root, options) {
    var map = mapFor(root);

    if (!root || !map || !options || !window.mapboxgl) {
      return null;
    }

    var lat = Number(options.lat);
    var lng = Number(options.lng);
    var count = Math.max(2, Math.floor(Number(options.count) || 0));
    var id = String(options.id || '').trim();

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !id) {
      return null;
    }

    root.__kidFriendClustersById = root.__kidFriendClustersById || Object.create(null);

    if (
      root.__kidFriendClustersById[id] &&
      typeof root.__kidFriendClustersById[id].remove === 'function'
    ) {
      root.__kidFriendClustersById[id].remove();
    }

    var element = document.createElement('div');

    element.className = 'kid-friend-cluster-shell';

    projectDomOverlayFamily(element, 'social', 'friend-cluster');

    projectDomOverlayState(element, 'default');

    var badge = document.createElement('span');

    badge.className = 'kid-friend-cluster';

    badge.setAttribute('data-kid-friend-cluster-count', String(count));

    var stars = document.createElement('span');

    stars.className = 'kid-friend-cluster__stars';

    stars.setAttribute('aria-hidden', 'true');

    for (var starIndex = 0; starIndex < 3; starIndex += 1) {
      var star = document.createElement('span');

      star.className = 'kid-friend-cluster__star';

      star.textContent = '★';

      stars.appendChild(star);
    }

    var countBadge = document.createElement('span');

    countBadge.className = 'kid-friend-cluster__count';

    countBadge.setAttribute('aria-hidden', 'true');

    countBadge.textContent = '×' + String(count);

    badge.appendChild(stars);
    badge.appendChild(countBadge);
    element.appendChild(badge);

    element.setAttribute(
      'aria-label',
      String(options.ariaLabel || count + ' friends at one station')
    );

    element.setAttribute('title', String(options.title || options.ariaLabel || ''));

    if (typeof options.onClick === 'function') {
      element.setAttribute('role', 'button');

      element.tabIndex = 0;

      function activate(event) {
        event.preventDefault();
        event.stopPropagation();
        options.onClick();
      }

      element.addEventListener('click', activate);

      element.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        activate(event);
      });
    }

    var marker = new window.mapboxgl.Marker({
      element: element,
      anchor: 'center',
    })
      .setLngLat([lng, lat])
      .addTo(map);

    root.__kidFriendClustersById[id] = marker;

    return marker;
  }

  function removeFriendMarker(root, id) {
    if (!root || !root.__kidSocialMarkersById || !root.__kidSocialMarkersById[id]) return false;
    var marker = root.__kidSocialMarkersById[id];
    marker.remove();
    delete root.__kidSocialMarkersById[id];
    return true;
  }

  // 5. 新增朋友星星 Marker
  function clearFriendMarkers(root) {
    if (!root || !root.__kidSocialMarkersById) {
      return;
    }

    Object.keys(root.__kidSocialMarkersById).forEach(function (id) {
      var marker = root.__kidSocialMarkersById[id];

      if (marker && typeof marker.remove === 'function') {
        marker.remove();
      }
    });

    root.__kidSocialMarkersById = Object.create(null);
  }

  function addFriendMarker(root, options) {
    var map = mapFor(root);

    if (!map || !options || !window.mapboxgl) {
      return null;
    }

    var lat = Number(options.lat);

    var lng = Number(options.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    var element = document.createElement('div');

    element.className = 'kid-custom-pin-shell';

    projectDomOverlayFamily(element, 'social', 'social-marker');
    projectDomOverlayState(element, 'default');

    element.innerHTML = options.html || '';

    if (options.ariaLabel) {
      element.setAttribute('aria-label', options.ariaLabel);

      element.setAttribute('title', options.ariaLabel);
    }

    if (typeof options.onClick === 'function') {
      element.setAttribute('role', 'button');

      element.tabIndex = 0;

      function activate(event) {
        event.preventDefault();
        event.stopPropagation();
        options.onClick();
      }

      element.addEventListener('click', activate);

      element.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        activate(event);
      });
    }

    var offset =
      Array.isArray(options.offset) &&
      options.offset.length >= 2 &&
      Number.isFinite(Number(options.offset[0])) &&
      Number.isFinite(Number(options.offset[1]))
        ? [Number(options.offset[0]), Number(options.offset[1])]
        : [0, 0];

    var marker = new window.mapboxgl.Marker({
      element: element,
      anchor: 'bottom',
      offset: offset,
    })
      .setLngLat([lng, lat])
      .addTo(map);

    if (options.id) {
      root.__kidSocialMarkersById = root.__kidSocialMarkersById || Object.create(null);

      root.__kidSocialMarkersById[options.id] = marker;
    }

    return marker;
  }

  window.LittleGKidMapRenderer = Object.freeze({
    version: VERSION,

    mount: mount,
    reportUnavailable: function (root) {
      setCanvasState(root, 'error');
    },
    destroy: destroy,
    resize: resize,
    setNightMode: setNightMode,

    mapFor: mapFor,
    enforceLayerOrder: enforceLayerOrder,

    getZoom: getZoom,
    setView: setView,
    flyTo: flyTo,
    fitBounds: fitBounds,
    panBy: panBy,
    ensureMarkerHorizontalSafe: ensureMarkerHorizontalSafe,

    zoomIn: zoomIn,
    zoomOut: zoomOut,

    setSelectedAreaSpotlight: setSelectedAreaSpotlight,

    clearSelectedAreaSpotlight: clearSelectedAreaSpotlight,

    setRelationshipLine: setRelationshipLine,

    clearRelationshipLine: clearRelationshipLine,

    setUserLocation: setUserLocation,

    clearUserLocation: clearUserLocation,

    addSearchMarker: addSearchMarker,

    clearSearchMarkers: clearSearchMarkers,

    setNearbyPoints: setNearbyPoints,

    clearNearbyPoints: clearNearbyPoints,

    setPublicExploreSelection: setPublicExploreSelection,

    addFriendMarker: addFriendMarker,

    removeFriendMarker: removeFriendMarker,

    clearFriendMarkers: clearFriendMarkers,

    addFriendCluster: addFriendCluster,

    clearFriendClusters: clearFriendClusters,

    setMarkerOverlayState: setMarkerOverlayState,
  });
})(window, document);
